/**
 * 좌측 사이드바: 검색·상태/타입 필터·카운트·섹션별 노드 목록 (설비는 공장별 그룹).
 * 항목 클릭 → store.requestFocusNode(id) (선택 + 캔버스 이동 요청).
 */
import { ChevronDown, ChevronRight, RotateCcw, Search, X } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FACTORIES } from "../../data/machines";
import { STATUS_LABEL_KO, formatThroughput, statusColorVar } from "../../lib/format";
import { filterNodes, selectActions, selectFilters, selectMode, selectNodes, selectSelectedNodeId, usePipelineStore, } from "../../store/pipeline-store";
import { ALL_NODE_STATUSES } from "../../types/pipeline";
import { useDebouncedCallback } from "./hooks";
import { Chip, IconButton, NodeTypeIcon, SectionTitle, Skeleton, StatusDot, cn, nodeTypeLabel } from "./ui";
const TYPE_CHIPS = ["source", "processor", "storage", "output"];
function countNodes(nodes) {
    const c = { ok: 0, warn: 0, error: 0, offline: 0 };
    for (const n of nodes) {
        const s = n.data.status;
        if (s === "healthy" || s === "running")
            c.ok++;
        else if (s === "warning" || s === "recovering")
            c.warn++;
        else if (s === "error")
            c.error++;
        else
            c.offline++;
    }
    return c;
}
const NodeRow = memo(function NodeRow({ node, selected, onSelect, }) {
    const d = node.data;
    return (<li>
      <button type="button" onClick={() => onSelect(node.id)} aria-current={selected ? "true" : undefined} title={`${d.label} · ${STATUS_LABEL_KO[d.status]} · ${formatThroughput(d.throughput, d.nodeType)}`} className={cn("flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1 text-left text-xs transition-colors", selected ? "bg-accent-soft text-fg" : "text-fg-muted hover:bg-surface-hover hover:text-fg", d.status === "offline" && !selected && "text-fg-subtle")} 
    // 노드 카드와 같은 팔레트: 좌측 상태 스트립 + (error/warning) 은은한 배경 틴트
    style={{
            boxShadow: `inset 3px 0 0 ${statusColorVar(d.status)}`,
            background: !selected && (d.status === "error" || d.status === "warning")
                ? `color-mix(in srgb, ${statusColorVar(d.status)} 10%, transparent)`
                : undefined,
        }}>
        <StatusDot status={d.status} pulse/>
        <NodeTypeIcon nodeType={d.nodeType} size={13}/>
        <span className="min-w-0 flex-1 truncate">{d.label}</span>
        <span className="tabular shrink-0 text-[11px] text-fg-subtle">{formatThroughput(d.throughput, d.nodeType)}</span>
      </button>
    </li>);
});
function Section({ title, count, open, onToggle, children, }) {
    return (<section>
      <button type="button" onClick={onToggle} aria-expanded={open} title={open ? `${title} 접기` : `${title} 펼치기`} className="flex w-full items-center gap-1 px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle hover:text-fg">
        {open ? <ChevronDown size={12} aria-hidden/> : <ChevronRight size={12} aria-hidden/>}
        <span className="truncate">{title}</span>
        <span className="tabular ml-auto font-normal">{count}</span>
      </button>
      {open && children}
    </section>);
}
export const SourceSidebar = memo(function SourceSidebar({ onNavigate, className }) {
    const nodes = usePipelineStore(selectNodes);
    const filters = usePipelineStore(selectFilters);
    const selectedId = usePipelineStore(selectSelectedNodeId);
    const mode = usePipelineStore(selectMode);
    const { setFilters, resetFilters, requestFocusNode } = usePipelineStore(selectActions);
    // 검색 입력 디바운스
    const [query, setQuery] = useState(filters.query);
    const pushQuery = useDebouncedCallback((q) => setFilters({ query: q }), 200);
    useEffect(() => {
        if (filters.query === "" && query !== "")
            setQuery("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.query]);
    const [open, setOpen] = useState({
        source: true,
        processor: true,
        storage: true,
        output: true,
        "f:vietnam": true,
        "f:dongtan": true,
    });
    const toggle = useCallback((k) => setOpen((o) => ({ ...o, [k]: !o[k] })), []);
    const filtered = useMemo(() => filterNodes(nodes, filters), [nodes, filters]);
    const counts = useMemo(() => countNodes(nodes), [nodes]);
    const grouped = useMemo(() => {
        const byFactory = { vietnam: [], dongtan: [] };
        const processor = [];
        const storage = [];
        const output = [];
        for (const n of filtered) {
            switch (n.data.nodeType) {
                case "source":
                    if (n.data.factoryId)
                        byFactory[n.data.factoryId].push(n);
                    break;
                case "processor":
                case "model":
                    processor.push(n);
                    break;
                case "storage":
                    storage.push(n);
                    break;
                case "output":
                    output.push(n);
                    break;
            }
        }
        return { byFactory, processor, storage, output };
    }, [filtered]);
    const onSelect = useCallback((id) => {
        requestFocusNode(id);
        onNavigate?.();
    }, [requestFocusNode, onNavigate]);
    const toggleStatus = (s) => setFilters({
        statuses: filters.statuses.includes(s) ? filters.statuses.filter((x) => x !== s) : [...filters.statuses, s],
    });
    const toggleType = (t) => setFilters({ types: filters.types.includes(t) ? filters.types.filter((x) => x !== t) : [...filters.types, t] });
    const hasFilter = filters.query !== "" || filters.statuses.length > 0 || filters.types.length > 0 || filters.factories.length > 0;
    const loading = mode === "idle" && nodes.length === 0;
    const sourceCount = grouped.byFactory.vietnam.length + grouped.byFactory.dongtan.length;
    return (<aside className={cn("flex h-full min-h-0 min-w-0 flex-col bg-bg-elevated", className)} aria-label="노드 목록">
      {/* 검색 */}
      <div className="border-b border-border p-2">
        <label className="relative block">
          <span className="sr-only">노드 검색</span>
          <Search aria-hidden size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-fg-subtle"/>
          <input type="search" value={query} onChange={(e) => {
            setQuery(e.target.value);
            pushQuery(e.target.value);
        }} placeholder="설비·노드 검색 (예: 01호기, M12, outbox)" title="라벨·id·설비번호 부분 일치" className="h-8 w-full rounded-md border border-border bg-bg pl-7 pr-7 text-xs text-fg placeholder:text-fg-subtle focus:border-border-strong focus:outline-none"/>
          {query && (<button type="button" aria-label="검색어 지우기" title="검색어 지우기" onClick={() => {
                setQuery("");
                setFilters({ query: "" });
            }} className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-fg-subtle hover:text-fg">
              <X size={12}/>
            </button>)}
        </label>

        {/* 카운트 */}
        <div className="mt-2 grid grid-cols-4 gap-1 text-center" aria-label="상태별 노드 수">
          {[
            ["정상", counts.ok, "var(--status-healthy)", "정상 + 수집 중"],
            ["주의", counts.warn, "var(--status-warning)", "경고 + 복구 중"],
            ["오류", counts.error, "var(--status-error)", "오류"],
            ["정지", counts.offline, "var(--status-offline)", "정지 (데이터 없음 포함)"],
        ].map(([label, n, color, tip]) => (<div key={label} className="rounded-md border border-border bg-bg px-1 py-1" title={tip}>
              <div className="tabular text-sm font-semibold leading-tight" style={{ color }}>
                {loading ? "—" : n}
              </div>
              <div className="text-[10px] text-fg-subtle">{label}</div>
            </div>))}
        </div>

        {/* 상태 필터 */}
        <div className="mt-2 flex flex-wrap gap-1" role="group" aria-label="상태 필터">
          {ALL_NODE_STATUSES.map((s) => (<Chip key={s} label={STATUS_LABEL_KO[s]} color={statusColorVar(s)} active={filters.statuses.includes(s)} onClick={() => toggleStatus(s)} title={`상태 필터: ${STATUS_LABEL_KO[s]} (다중 선택)`}/>))}
        </div>
        {/* 타입 필터 */}
        <div className="mt-1 flex flex-wrap items-center gap-1" role="group" aria-label="타입 필터">
          {TYPE_CHIPS.map((t) => (<Chip key={t} label={nodeTypeLabel(t)} active={filters.types.includes(t)} onClick={() => toggleType(t)} title={`타입 필터: ${nodeTypeLabel(t)} (다중 선택)`}/>))}
          {hasFilter && (<IconButton label="필터 초기화" onClick={resetFilters} className="ml-auto size-6">
              <RotateCcw size={12}/>
            </IconButton>)}
        </div>
      </div>

      {/* 목록 */}
      <div className="thin-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1 pb-2">
        {loading ? (<div className="space-y-2 p-2" aria-busy="true" aria-label="노드 목록 불러오는 중">
            {Array.from({ length: 12 }, (_, i) => (<Skeleton key={i} className="h-6 w-full"/>))}
          </div>) : filtered.length === 0 ? (<div className="p-4 text-center text-xs text-fg-subtle">
            {nodes.length === 0 ? "표시할 노드가 없습니다." : "조건에 맞는 노드가 없습니다."}
            {hasFilter && (<button type="button" onClick={resetFilters} className="mt-2 block w-full text-accent hover:underline">
                필터 초기화
              </button>)}
          </div>) : (<>
            <Section title="데이터 소스 (설비)" count={sourceCount} open={open.source} onToggle={() => toggle("source")}>
              {FACTORIES.map((f) => {
                const list = grouped.byFactory[f.id];
                if (list.length === 0)
                    return null;
                const key = `f:${f.id}`;
                return (<div key={f.id}>
                    <button type="button" onClick={() => toggle(key)} aria-expanded={open[key]} title={open[key] ? `${f.name} 접기` : `${f.name} 펼치기`} className="flex w-full items-center gap-1 px-2 py-1 text-[11px] font-medium text-fg-muted hover:text-fg">
                      {open[key] ? <ChevronDown size={12} aria-hidden/> : <ChevronRight size={12} aria-hidden/>}
                      <span className="truncate">{f.name}</span>
                      <span className="tabular ml-auto text-fg-subtle">{list.length}대</span>
                    </button>
                    {open[key] && (<ul className="space-y-px pl-1">
                        {list.map((n) => (<NodeRow key={n.id} node={n} selected={n.id === selectedId} onSelect={onSelect}/>))}
                      </ul>)}
                  </div>);
            })}
            </Section>
            <Section title="처리 단계" count={grouped.processor.length} open={open.processor} onToggle={() => toggle("processor")}>
              <ul className="space-y-px">
                {grouped.processor.map((n) => (<NodeRow key={n.id} node={n} selected={n.id === selectedId} onSelect={onSelect}/>))}
              </ul>
            </Section>
            <Section title="저장소" count={grouped.storage.length} open={open.storage} onToggle={() => toggle("storage")}>
              <ul className="space-y-px">
                {grouped.storage.map((n) => (<NodeRow key={n.id} node={n} selected={n.id === selectedId} onSelect={onSelect}/>))}
              </ul>
            </Section>
            <Section title="출력" count={grouped.output.length} open={open.output} onToggle={() => toggle("output")}>
              <ul className="space-y-px">
                {grouped.output.map((n) => (<NodeRow key={n.id} node={n} selected={n.id === selectedId} onSelect={onSelect}/>))}
              </ul>
            </Section>
          </>)}
      </div>
      <SectionTitle right={<span className="tabular font-normal normal-case">{filtered.length}/{nodes.length}</span>}>
        표시 노드
      </SectionTitle>
    </aside>);
});
